import type {
	DependencyHealthAttributes,
	DependencyHealthDetails,
	DependencyHealthReporter,
	DependencyOutcome,
} from '../dependencyHealthAnalytics.ts';

export type DependencyHealthContext = Pick<DependencyHealthAttributes, 'dependency' | 'operation'>;

export const dependencyHealthTime = (): number | undefined => {
	try {
		const time = performance.now();
		return Number.isFinite(time) ? time : undefined;
	} catch {
		return undefined;
	}
};

export const dependencyHealthDuration = (startedAt: number | undefined): number | undefined => {
	const finishedAt = dependencyHealthTime();
	if (startedAt === undefined || finishedAt === undefined) {
		return undefined;
	}
	return Math.max(0, Math.round(finishedAt - startedAt));
};

export const reportDependencyHealth = (
	report: DependencyHealthReporter | undefined,
	attributes: DependencyHealthAttributes,
): void => {
	try {
		report?.(attributes);
	} catch {
		// A failed measurement must not turn a successful dependency read into a failure.
	}
};

export type DependencyHealthObservation = {
	finish: (
		outcome: DependencyOutcome,
		details?: DependencyHealthDetails,
	) => DependencyHealthAttributes | undefined;
};

export const createDependencyHealthObservation = (
	report: DependencyHealthReporter | undefined,
	context: DependencyHealthContext,
): DependencyHealthObservation => {
	const startedAt = report ? dependencyHealthTime() : undefined;
	let finished = false;
	return {
		finish: (outcome, details = {}) => {
			if (finished) {
				return undefined;
			}
			finished = true;
			const durationMs = startedAt === undefined ? undefined : dependencyHealthDuration(startedAt);
			const attributes = {
				...context,
				outcome,
				...details,
				...(durationMs === undefined ? {} : { durationMs }),
			};
			reportDependencyHealth(report, attributes);
			return attributes;
		},
	};
};
